import { PDFDocument, PDFFont, PDFPage, PageSizes } from "pdf-lib";
import { SourcingOption } from "../constants";
import { Dimension, PageContent, PageSize, TemplateContent } from "../types";
import { drawTemplateOnPage } from "./drawTemplateOnPage";
import {
  fetchImage,
  getXPosition,
  getYPosition,
  sanitizeAndConvertText,
  wrapText,
} from "./helpers";

export const drawBOMPage = async (
  pdfDoc: PDFDocument,
  font: PDFFont,
  data: PageContent,
  size: PageSize,
  imageDimension?: Dimension,
  templateData?: TemplateContent,
  contentUrl?: string
): Promise<PDFPage> => {
  try {
    let pageDimensions: [number, number] =
      templateData?.orientation === "landscape"
        ? [PageSizes[size as PageSize][1], PageSizes[size as PageSize][0]]
        : PageSizes[size];

    let page = pdfDoc.addPage(pageDimensions);

    // Get the width and height of the first page
    const { width, height } = page.getSize();

    const sourcingType =
      SourcingOption.find(
        (d) => d?.value === (data?.sourcing_type || data?.bom?.sourcing_type)
      )?.label || "";

    let startXPosition = getXPosition(
      width,
      templateData?.margin?.width_offset || 5
    );
    let startYPosition = getYPosition(
      height,
      templateData?.margin?.top_offset || 5
    );

    let contentStartPositionY = height - startYPosition;

    let contentLeftPosition = startXPosition + 1;
    let contentCenterPosition = (width - startXPosition) / 2 - 20;

    let mainContentStartPositionX = contentLeftPosition + 10;

    let y = contentStartPositionY - 90;

    page.drawText(`Basic Details`, {
      x: contentCenterPosition,
      y: contentStartPositionY - 20,
      size: 16,
    });
    page.drawText(
      `BOM ID & Revision Number: ${
        data?.sequence_id || data?.bom?.sequence_id || "..."
      }-R${data?.version || data?.bom?.version || 0}`,
      { x: mainContentStartPositionX, y: contentStartPositionY - 50, size: 12 }
    );
    page.drawText(
      `BOM Short Description: ${
        data?.short_description || data?.bom?.short_description || "---"
      }`,
      {
        x: mainContentStartPositionX,
        y: contentStartPositionY - 70,
        size: 12,
      }
    );

    let group_name = data?.group_name || data?.bom?.group_name;

    if (group_name) {
      page.drawText(`BOM Group: ${group_name}`, {
        x: mainContentStartPositionX,
        y: y,
        size: 12,
      });
      y -= 20;
    }

    let noun_name = data?.noun_name || data?.bom?.noun_name;

    if (noun_name) {
      page.drawText(`BOM Tag: ${noun_name}`, {
        x: mainContentStartPositionX,
        y: y,
        size: 12,
      });
      y -= 20;
    }

    let reference_id = data?.reference_id || data?.bom?.reference_id;

    if (reference_id) {
      page.drawText(`Reference Number: ${reference_id}`, {
        x: mainContentStartPositionX,
        y: y,
        size: 12,
      });
      y -= 20;
    }

    if (sourcingType) {
      page.drawText(`Sourcing Type: ${sourcingType}`, {
        x: mainContentStartPositionX,
        y: y,
        size: 12,
      });
      y -= 20;
    }

    let vendor_name =
      data?.vendor?.vendor_name || data?.bom?.vendor?.vendor_name;

    if (vendor_name) {
      page.drawText(`Supplier Name: ${vendor_name}`, {
        x: mainContentStartPositionX,
        y: y,
        size: 12,
      });
      y -= 20;
    }

    let manufacturer_name =
      data?.vendor?.manufacturer_name || data?.bom?.vendor?.manufacturer_name;

    if (manufacturer_name) {
      page.drawText(`Manufacturer Name: ${manufacturer_name}`, {
        x: mainContentStartPositionX,
        y: y,
        size: 12,
      });
      y -= 20;
    }

    let part_number =
      data?.vendor?.part_number || data?.bom?.vendor?.part_number;

    if (part_number) {
      page.drawText(`Manufacturer Number: ${part_number}`, {
        x: mainContentStartPositionX,
        y: y,
        size: 12,
      });
      y -= 20;
    }

    let unit_code =
      data?.organization_unit?.unit_code ||
      data?.bom?.organization_unit?.unit_code;

    if (unit_code) {
      page.drawText(`Unit Name: ${unit_code}`, {
        x: mainContentStartPositionX,
        y: y,
        size: 12,
      });
      y -= 20;
    }

    let unit_location =
      data?.organization_unit?.unit_location ||
      data?.bom?.organization_unit?.unit_location;

    if (unit_location) {
      page.drawText(`Unit Location: ${unit_location}`, {
        x: mainContentStartPositionX,
        y: y,
        size: 12,
      });
      y -= 20;
    }

    let other_details =
      data?.organization_unit?.other_details ||
      data?.bom?.organization_unit?.other_details;

    if (other_details) {
      page.drawText(`Other Details: ${other_details}`, {
        x: mainContentStartPositionX,
        y: y,
        size: 12,
      });
      y -= 20;
    }

    let serial_num = data?.serial_num || data?.bom?.serial_num;

    if (serial_num) {
      page.drawText(`Tag Number: ${serial_num}`, {
        x: mainContentStartPositionX,
        y: y,
        size: 12,
      });
      y -= 20;
    }

    let normal_quantity = data?.normal_quantity || data?.bom?.normal_quantity;

    if (normal_quantity) {
      page.drawText(`Default Quantity: ${normal_quantity}`, {
        x: mainContentStartPositionX,
        y: y,
        size: 12,
      });
      y -= 20;
    }

    let spare_part = data?.spare_part || data?.bom?.spare_part ? "Yes" : "No";

    if (data?.spare_part || data?.bom?.spare_part) {
      page.drawText(`Spare Part: ${spare_part}`, {
        x: mainContentStartPositionX,
        y: y,
        size: 12,
      });
      y -= 20;
    }

    let tagsInfo = data?.tagsInfo || data?.bom?.tagsInfo;

    let spare_part_category = tagsInfo?.find(
      (tag) =>
        tag.tag_group_name.toLowerCase() === "Spare Part Category".toLowerCase()
    )?.tag_name;

    if (spare_part_category) {
      page.drawText(`Spare Part Category: ${spare_part_category}`, {
        x: mainContentStartPositionX,
        y: y,
        size: 12,
      });
      y -= 20;
    }

    let spare_part_quantity =
      data?.spare_part_quantity || data?.bom?.spare_part_quantity;

    if (spare_part_quantity) {
      page.drawText(`Spare Part Quantity: ${spare_part_quantity}`, {
        x: mainContentStartPositionX,
        y: y,
        size: 12,
      });
      y -= 20;
    }

    let bom_status = tagsInfo?.find(
      (tag) =>
        tag.tag_group_name.toLowerCase() === "Child BOM Status".toLowerCase()
    )?.tag_name;

    if (bom_status) {
      page.drawText(`BOM Status: ${bom_status}`, {
        x: mainContentStartPositionX,
        y: y,
        size: 12,
      });
      y -= 20;
    }

    const notes = data?.bom?.notes || data?.notes || "";

    if (notes) {
      const lines = wrapText(
        sanitizeAndConvertText(notes), // Simplified the repetition
        width -
          2 * (((templateData?.margin?.width_offset || 5) * width) / 100) -
          50,
        12,
        font
      );

      let currentY = y;
      const lineHeight = font.heightAtSize(12 * 1.5); // Adjust line height as needed

      for (const line of lines) {
        if (currentY - lineHeight < startYPosition) {
          // Reached the maximum height, add a new page
          // Render Template
          await drawTemplateOnPage(
            pdfDoc,
            page,
            templateData,
            font,
            false,
            data
          );

          page = pdfDoc.addPage(pageDimensions);
          currentY = height - startYPosition - 50; // Reset currentY for the new page, adjust margin as needed
        }

        page.drawText(`${line === lines[0] ? "Notes: " : ""}${line}`, {
          x: mainContentStartPositionX,
          y: currentY,
          size: 12,
          font,
        });

        currentY -= lineHeight; // Move currentY down for the next line
      }
      // page.drawText(`Notes: ${data?.notes}`, {
      //   x: mainContentStartPositionX,
      //   y: y,
      //   size: 12,
      // });
      y = currentY;
    }

    let contentImage = null;

    await fetchImage(contentUrl)
      .then(async (contentBytes) => {
        contentImage = contentUrl?.includes("png")
          ? await pdfDoc.embedPng(contentBytes)
          : await pdfDoc.embedJpg(contentBytes);
      })
      .catch(async (error: any) => {
        console.error(`Error loading content image on preview: ${error}`);
      });

    if (contentImage) {
      const imageWidth = (imageDimension?.width || 150) * 0.75;
      const imageHeight = (imageDimension?.height || 150) * 0.75;

      page.drawImage(contentImage, {
        x: mainContentStartPositionX,
        y: y - imageHeight,
        width: imageWidth,
        height: imageHeight,
      });
    }

    // Render Template
    await drawTemplateOnPage(pdfDoc, page, templateData, font, true, data);

    return page;
  } catch (error: any) {
    console.error(`Error loading bom on preview: ${error}`);
    // return await drawNoPreviewPage(pdfDoc, size);
  }
};
